import { useState, useEffect } from "react";
import { Activity, Heart, Flame, Footprints, TrendingUp, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, AreaChart, Area } from "recharts";
import { cn } from "@/lib/utils";
import { format, subDays } from "date-fns";
import type { HealthDataEntry } from "@/hooks/useHealthKit";

interface HealthDashboardProps {
  isConnected: boolean;
  platformName: string;
  onConnect?: () => void;
  readSteps: (days?: number) => Promise<HealthDataEntry[] | null>;
  readHeartRate: (days?: number) => Promise<HealthDataEntry[] | null>;
  readActiveEnergy: (days?: number) => Promise<HealthDataEntry[] | null>;
}

interface HealthMetric {
  date: string;
  value: number;
  label: string;
}

export function HealthDashboard({
  isConnected,
  platformName,
  onConnect,
  readSteps,
  readHeartRate,
  readActiveEnergy,
}: HealthDashboardProps) {
  const [stepsData, setStepsData] = useState<HealthMetric[]>([]);
  const [heartRateData, setHeartRateData] = useState<HealthMetric[]>([]);
  const [caloriesData, setCaloriesData] = useState<HealthMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [todayStats, setTodayStats] = useState({
    steps: 0,
    heartRate: 0,
    calories: 0,
  });

  const fetchHealthData = async () => {
    if (!isConnected) return;
    
    setLoading(true);
    try {
      const [steps, heartRate, activeEnergy] = await Promise.all([
        readSteps(7),
        readHeartRate(7),
        readActiveEnergy(7),
      ]);

      // Process steps
      if (steps) {
        const processedSteps = steps.map(entry => ({
          date: format(new Date(entry.date), 'EEE'),
          value: Math.round(entry.value),
          label: format(new Date(entry.date), 'MMM d'),
        }));
        setStepsData(processedSteps);
        const todaySteps = steps[steps.length - 1]?.value || 0;
        setTodayStats(prev => ({ ...prev, steps: Math.round(todaySteps) }));
      }

      // Process heart rate
      if (heartRate) {
        const processedHR = heartRate.map(entry => ({
          date: format(new Date(entry.date), 'EEE'),
          value: Math.round(entry.value),
          label: format(new Date(entry.date), 'MMM d'),
        }));
        setHeartRateData(processedHR);
        const avgHR = heartRate.reduce((sum, e) => sum + e.value, 0) / heartRate.length;
        setTodayStats(prev => ({ ...prev, heartRate: Math.round(avgHR) }));
      }

      // Process active energy
      if (activeEnergy) {
        const processedCals = activeEnergy.map(entry => ({
          date: format(new Date(entry.date), 'EEE'),
          value: Math.round(entry.value),
          label: format(new Date(entry.date), 'MMM d'),
        }));
        setCaloriesData(processedCals);
        const todayCals = activeEnergy[activeEnergy.length - 1]?.value || 0;
        setTodayStats(prev => ({ ...prev, calories: Math.round(todayCals) }));
      }
    } catch (error) {
      console.error('Failed to fetch health data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchHealthData();
    }
  }, [isConnected]);

  if (!isConnected) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center mb-3">
            <Heart className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="font-medium mb-1">Connect {platformName}</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xs">
            View your steps, heart rate, and active calories with health sync
          </p>
          {onConnect && (
            <Button onClick={onConnect} variant="outline" size="sm">
              Connect
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          Health Dashboard
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchHealthData}
          disabled={loading}
          className="h-8 px-2"
        >
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
        </Button>
      </div>

      {/* Today's stats cards */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Footprints className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">Steps</span>
            </div>
            <p className="text-xl font-bold text-blue-600">
              {todayStats.steps.toLocaleString()}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-red-500" />
              <span className="text-xs text-muted-foreground">Avg HR</span>
            </div>
            <p className="text-xl font-bold text-red-600">
              {todayStats.heartRate} <span className="text-xs font-normal">bpm</span>
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/20">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-1">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-muted-foreground">Active Cal</span>
            </div>
            <p className="text-xl font-bold text-orange-600">
              {todayStats.calories.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Steps Chart */}
      {stepsData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Footprints className="w-4 h-4 text-blue-500" />
              Steps (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stepsData}>
                  <defs>
                    <linearGradient id="stepsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(217, 91%, 60%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis hide />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.[0]) {
                        return (
                          <div className="bg-popover border rounded-lg px-3 py-2 shadow-lg">
                            <p className="text-xs text-muted-foreground">{payload[0].payload.label}</p>
                            <p className="font-bold text-blue-600">{payload[0].value?.toLocaleString()} steps</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(217, 91%, 60%)"
                    strokeWidth={2}
                    fill="url(#stepsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Heart Rate Chart */}
      {heartRateData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />
              Heart Rate (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={heartRateData}>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.[0]) {
                        return (
                          <div className="bg-popover border rounded-lg px-3 py-2 shadow-lg">
                            <p className="text-xs text-muted-foreground">{payload[0].payload.label}</p>
                            <p className="font-bold text-red-600">{payload[0].value} bpm</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(0, 84%, 60%)"
                    strokeWidth={2}
                    dot={{ fill: 'hsl(0, 84%, 60%)', strokeWidth: 0, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Calories Chart */}
      {caloriesData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Active Calories (7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={caloriesData}>
                  <defs>
                    <linearGradient id="calsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="hsl(24, 95%, 53%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false}
                    tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis hide />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.[0]) {
                        return (
                          <div className="bg-popover border rounded-lg px-3 py-2 shadow-lg">
                            <p className="text-xs text-muted-foreground">{payload[0].payload.label}</p>
                            <p className="font-bold text-orange-600">{payload[0].value?.toLocaleString()} kcal</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="hsl(24, 95%, 53%)"
                    strokeWidth={2}
                    fill="url(#calsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* No data state */}
      {stepsData.length === 0 && heartRateData.length === 0 && caloriesData.length === 0 && !loading && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-6 text-center">
            <AlertCircle className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No health data available yet. Make sure {platformName} is syncing data.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
